import { useState } from "react";
import { Store, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mfaToken) {
        await verifyMfa(mfaToken, code);
      } else {
        const data = await login(username, password);
        if (data?.mfa_required) setMfaToken(data.mfa_token);
      }
    } catch (err) {
      if (mfaToken && err?.response?.status === 401 && err?.response?.data?.detail !== "Codice non corretto") { setMfaToken(""); setCode(""); }
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg)]">
      <div className="hidden lg:block relative overflow-hidden bg-[var(--ink)]">
        <img src="/login-hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-xs uppercase tracking-[0.25em] text-[var(--primary)] font-semibold mb-3">Conto vendita</p>
          <h1 className="font-heading text-5xl font-bold leading-tight">Ogni oggetto ha<br />una seconda storia.</h1>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-[var(--ink)] text-[var(--primary)] grid place-items-center"><Store className="h-6 w-6" /></div>
            <div>
              <h2 className="font-heading text-2xl font-bold">Accedi</h2>
              <p className="text-sm text-[var(--muted)]">Gestionale Mercatino dell'Usato</p>
            </div>
          </div>
          {mfaToken ? (
            <div className="space-y-2" data-testid="mfa-step">
              <Label htmlFor="login-code">Codice di verifica</Label>
              <Input id="login-code" data-testid="login-mfa-code-input" inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className="h-12 text-base tracking-widest" required />
              <p className="text-xs text-[var(--muted)]">Apri l'app di autenticazione sul telefono e inserisci il codice a 6 cifre. Se hai perso il telefono usa un codice di recupero (es. A1B2-C3D4).</p>
              <button type="button" className="text-xs underline text-[var(--muted)]" onClick={() => { setMfaToken(""); setCode(""); setError(""); }} data-testid="mfa-back-button">Torna al login</button>
            </div>
          ) : (<>
          <div className="space-y-2">
            <Label htmlFor="login-id">ID</Label>
            <Input id="login-id" data-testid="login-id-input" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="es. darkalex931@gmail.com" value={username} onChange={(e) => setUsername(e.target.value)} className="h-12 text-base" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-pw">Password</Label>
            <Input id="login-pw" type="password" autoCapitalize="none" autoCorrect="off" data-testid="login-password-input" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 text-base" required />
          </div>
          </>)}
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit-button" className="btn-primary w-full h-12">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mfaToken ? "Verifica" : "Entra"}
          </button>
          <p className="text-xs text-[var(--muted)]">Gli account vengono creati dall'amministratore. Le maiuscole nella password contano.</p>
        </form>
      </div>
    </div>
  );
}
